import { DialogContent, Stack, type Theme } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { BottomSheet, Text, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import parachuteImage from "@src/appV2/redesign/WorkerShifts/ShiftDetails/components/CancellationProtection/assets/images/parachute.png";

import { type ResolvedCancellationProtectionPayout } from "../api/types";
import { getTotalPayoutAmountInMinorUnits } from "../api/utils";
import { PayoutShiftCard } from "./PayoutShiftCard";

interface CancellationProtectionPayoutBottomSheetProps {
  modalState: UseModalState;
  payout: ResolvedCancellationProtectionPayout;
  isUpdating: boolean;
  onClose: () => void;
}

export function CancellationProtectionPayoutBottomSheet(
  props: CancellationProtectionPayoutBottomSheetProps
) {
  const { modalState, payout, isUpdating, onClose } = props;

  useLogEffect(
    APP_V2_USER_EVENTS.CANCELLATION_PROTECTION_PAYOUT_VIEWED,
    { payoutId: payout.id },
    { enabled: modalState.modalIsOpen }
  );

  return (
    <BottomSheet
      disableSwipeArea
      modalState={modalState}
      canDismiss={false}
      paperSx={(theme: Theme) => ({
        backgroundColor: theme.palette.background.quaternary,
      })}
      header={<BottomSheetHeader closeButtonDisabled={isUpdating} onClose={onClose} />}
      footer={
        <DialogFooter orientation="vertical">
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            isLoading={isUpdating}
            onClick={onClose}
          >
            Great!
          </LoadingButton>
        </DialogFooter>
      }
    >
      <DialogContent
        sx={{
          paddingX: 5,
        }}
      >
        <Stack spacing={8} alignItems="center">
          <img src={parachuteImage} alt="" width={105} height={120} />
          <Text
            semibold
            variant="h4"
            sx={(theme: Theme) => ({
              color: theme.palette.text.primary,
              textAlign: "center",
            })}
          >
            {`You'll receive ${formatCentsAsUsd(getTotalPayoutAmountInMinorUnits(payout))} due to Cancellation Protection`}
          </Text>
          <Stack spacing={5} alignItems="center" sx={{ width: "100%" }}>
            <PayoutShiftCard payout={payout} />
            <Text
              variant="body1"
              sx={(theme: Theme) => ({
                color: theme.palette.text.primary,
                textAlign: "center",
              })}
            >
              No action needed
            </Text>
          </Stack>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
